/**
 * TypeScript mirror of the context-graph read contract — PINNED.
 *
 * Source of truth: `backend/app/modules/context_graph/schemas/graph.py`,
 * served by `GET /api/v3/context-graph/rooms/{project_id}`,
 * `GET /api/v3/context-graph/chats/{chat_public_id}/messages/{message_id}` and
 * `GET /api/v3/context-graph/chats/{chat_public_id}` (the whole conversation:
 * every answer's graph in the chat, unioned).
 *
 * Node ids are namespaced by kind (`company:<id>`, `doc:<id>`, `clause:…`,
 * `metric:…`). Timestamps are epoch-ms ints. Hand-rolled boundary validation
 * follows the `cabinet.ts` convention — the repo does not use Zod in services.
 */

export type ContextNodeKind =
	| "fund"
	| "company"
	| "period"
	| "document"
	| "clause"
	| "metric"
	| "covenant"
	| "status";

export type ContextRelation =
	| "HOLDS"
	| "AS_OF"
	| "GOVERNED_BY"
	| "REPORTS"
	| "CERTIFIES"
	| "SUPERSEDES"
	| "HAS_METRIC"
	| "DEFINES"
	| "SETS_BASIS"
	| "ADD_BACKS"
	| "SOURCE"
	| "COMPUTES"
	| "TESTED_WITH"
	| "SUBJECT_TO"
	| "RESULTS_IN"
	| "EXCLUDED"
	| "HEADROOM"
	| "UNDER"
	| "ABOUT";

export type ContextGraphScope = "room" | "message" | "chat";

export const CONTEXT_GRAPH_SCOPES: readonly ContextGraphScope[] = ["room", "message", "chat"];

export interface ContextNode {
	id: string;
	kind: ContextNodeKind;
	label: string;
	/** Second line on the card — "Portfolio company", "$24.0M". */
	sublabel?: string | null;
	/** documents: current | superseded; status nodes: breach | watch | compliant. */
	status?: string | null;
	/** Small chip — "FINAL", "v2 · signed", "2 superseded drafts". */
	badge?: string | null;
	/** Sub-style within a kind, e.g. "ebitda" on a metric. */
	variant?: string | null;
	/** `doppler://pdf/<file_id>?page=N&hl=<quote>` or `doppler://pdf/<file_id>`. */
	href?: string | null;
	meta: Record<string, unknown>;
}

export interface ContextEdge {
	id: string;
	src: string;
	dst: string;
	relation: ContextRelation;
	/** Part of the answer's citation chain — drawn as the reasoning path. */
	on_path: boolean;
	label?: string | null;
	meta: Record<string, unknown>;
}

export interface ContextGraph {
	generated_at: number | null;
	scope: ContextGraphScope;
	/** Node id to centre on, or null for the whole room. */
	focus: string | null;
	nodes: ContextNode[];
	edges: ContextEdge[];
	/**
	 * How the response was derived, when that is not self-evident. Room scope
	 * sets `scoped_by`: "corpus_root" when the catalog was selected by the
	 * project's own staging root, "deal_name" when it fell back to matching the
	 * room's name because that project has staged nothing.
	 *
	 * Optional on the type, always present from `parseContextGraph`: the
	 * backend sends it on every response, but a hand-built graph (a fixture, a
	 * layout test) has no derivation to describe.
	 */
	meta?: Record<string, unknown>;
}

export const CONTEXT_NODE_KINDS: readonly ContextNodeKind[] = [
	"fund",
	"company",
	"period",
	"document",
	"clause",
	"metric",
	"covenant",
	"status",
];

export const CONTEXT_RELATIONS: readonly ContextRelation[] = [
	"HOLDS",
	"AS_OF",
	"GOVERNED_BY",
	"REPORTS",
	"CERTIFIES",
	"SUPERSEDES",
	"HAS_METRIC",
	"DEFINES",
	"SETS_BASIS",
	"ADD_BACKS",
	"SOURCE",
	"COMPUTES",
	"TESTED_WITH",
	"SUBJECT_TO",
	"RESULTS_IN",
	"EXCLUDED",
	"HEADROOM",
	"UNDER",
	"ABOUT",
];

/** "GOVERNED_BY" → "GOVERNED BY" — the on-canvas edge label. */
export function relationLabel(edge: Pick<ContextEdge, "relation" | "label">): string {
	return edge.label ?? edge.relation.replace(/_/g, " ");
}

// ---------------------------------------------------------------------------
// Boundary validation (dependency-free — matches cabinet.ts style)
// ---------------------------------------------------------------------------

function isRecord(value: unknown): value is Record<string, unknown> {
	return typeof value === "object" && value !== null && !Array.isArray(value);
}

function optionalString(value: unknown): string | null {
	return typeof value === "string" ? value : null;
}

function optionalNumber(value: unknown): number | null {
	return typeof value === "number" && Number.isFinite(value) ? value : null;
}

function parseNode(raw: unknown): ContextNode | null {
	if (!isRecord(raw)) return null;
	if (typeof raw.id !== "string" || raw.id.length === 0) return null;
	if (typeof raw.label !== "string") return null;
	if (
		typeof raw.kind !== "string" ||
		!CONTEXT_NODE_KINDS.includes(raw.kind as ContextNodeKind)
	) {
		return null;
	}
	return {
		id: raw.id,
		kind: raw.kind as ContextNodeKind,
		label: raw.label,
		sublabel: optionalString(raw.sublabel),
		status: optionalString(raw.status),
		badge: optionalString(raw.badge),
		variant: optionalString(raw.variant),
		href: optionalString(raw.href),
		meta: isRecord(raw.meta) ? raw.meta : {},
	};
}

function parseEdge(raw: unknown): ContextEdge | null {
	if (!isRecord(raw)) return null;
	if (typeof raw.src !== "string" || typeof raw.dst !== "string") return null;
	if (
		typeof raw.relation !== "string" ||
		!CONTEXT_RELATIONS.includes(raw.relation as ContextRelation)
	) {
		return null;
	}
	return {
		id: typeof raw.id === "string" && raw.id ? raw.id : `${raw.src}->${raw.dst}`,
		src: raw.src,
		dst: raw.dst,
		relation: raw.relation as ContextRelation,
		on_path: raw.on_path === true,
		label: optionalString(raw.label),
		meta: isRecord(raw.meta) ? raw.meta : {},
	};
}

/**
 * Structurally validate a context-graph payload. `null` when the envelope is
 * wrong or any node is unidentifiable; malformed edges and edges whose endpoints
 * are missing are dropped instead.
 */
export function parseContextGraph(input: unknown): ContextGraph | null {
	if (!isRecord(input)) return null;
	if (!Array.isArray(input.nodes) || !Array.isArray(input.edges)) return null;
	if (
		typeof input.scope !== "string" ||
		!CONTEXT_GRAPH_SCOPES.includes(input.scope as ContextGraphScope)
	) {
		return null;
	}

	const nodes: ContextNode[] = [];
	for (const raw of input.nodes) {
		const node = parseNode(raw);
		if (node === null) return null;
		nodes.push(node);
	}

	const ids = new Set(nodes.map((node) => node.id));
	const edges: ContextEdge[] = [];
	for (const raw of input.edges) {
		const edge = parseEdge(raw);
		if (edge === null) continue;
		if (!ids.has(edge.src) || !ids.has(edge.dst)) continue;
		edges.push(edge);
	}

	const focus = optionalString(input.focus);
	return {
		generated_at: optionalNumber(input.generated_at),
		scope: input.scope as ContextGraphScope,
		focus: focus !== null && ids.has(focus) ? focus : null,
		nodes,
		edges,
		meta: isRecord(input.meta) ? input.meta : {},
	};
}
